import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { UT_LOGO } from '../primitives';
import { STRENGTH_LABELS, matchState, passwordScore } from './passwordStrength';

/** Single 400px column with the faint logo watermark, as in the account handoff. */
export const UtAuthCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <section className={clsx('relative mx-auto w-full max-w-[400px] overflow-hidden py-6', className)}>
    <img src={UT_LOGO} alt="" aria-hidden className="pointer-events-none absolute -right-[90px] top-0 w-[380px] max-w-none opacity-[0.05]" />
    <div className="relative flex flex-col gap-4">{children}</div>
  </section>
);

/** Gold eyebrow, big condensed title, muted line underneath. */
export const AuthHeading: React.FC<{
  eyebrow?: string;
  title: React.ReactNode;
  sub?: React.ReactNode;
  size?: 'lg' | 'md' | 'sm';
  bad?: boolean;
}> = ({ eyebrow, title, sub, size = 'md', bad }) => (
  <div className="flex flex-col gap-1.5">
    {eyebrow && <span className="font-ut-display text-xs font-bold tracking-[0.16em] text-ut-acc">{eyebrow}</span>}
    <h1
      className={clsx(
        'font-ut-display font-extrabold uppercase leading-[0.95]',
        size === 'lg' ? 'text-[40px]' : size === 'md' ? 'text-[36px]' : 'text-[34px]',
        bad && 'text-ut-bad',
      )}
    >
      {title}
    </h1>
    {sub && <span className="text-[15px] leading-normal text-ut-mut">{sub}</span>}
  </div>
);

/** Inline notice: red for a failed attempt, gold for "do this next". */
export const AuthNotice: React.FC<{ tone: 'bad' | 'acc'; children: React.ReactNode }> = ({ tone, children }) => (
  <div
    role={tone === 'bad' ? 'alert' : 'status'}
    className={clsx(
      'rounded-[10px] border px-3 py-[11px] text-[13px] leading-[1.45]',
      tone === 'bad' ? 'border-ut-bad bg-ut-bad/10' : 'border-ut-acc bg-ut-acc-soft',
    )}
  >
    {children}
  </div>
);

const labelCls = 'flex flex-col gap-[5px] text-[11px] font-semibold uppercase tracking-[0.1em] text-ut-mut';
const sideLinkCls = 'text-xs font-semibold normal-case tracking-normal text-ut-acc hover:text-ut-ink';

/** Label with an optional link on the right ("Forgot?"). */
const FieldLabel: React.FC<{ label: React.ReactNode; side?: React.ReactNode }> = ({ label, side }) => (
  <span className="flex items-center justify-between gap-2">
    {label}
    {side}
  </span>
);

/** Small gold link for the right of a field label. */
export const SideLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
  <Link to={to} className={sideLinkCls}>
    {children}
  </Link>
);

/** Password input with a SHOW / HIDE toggle inside the field. */
export const PasswordInput: React.FC<
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: React.ReactNode; side?: React.ReactNode; invalid?: boolean }
> = ({ label, side, invalid, className, ...rest }) => {
  const [show, setShow] = useState(false);
  return (
    <label className={labelCls}>
      <FieldLabel label={label} side={side} />
      <span
        className={clsx(
          'flex items-center rounded-[10px] border bg-ut-field transition focus-within:border-ut-acc',
          invalid ? 'border-ut-bad' : 'border-ut-line',
        )}
      >
        <input
          {...rest}
          type={show ? 'text' : 'password'}
          aria-invalid={invalid || undefined}
          className={clsx(
            'min-w-0 flex-1 border-0 bg-transparent px-3.5 py-[13px] font-ut-body text-[15px] font-medium normal-case tracking-normal text-ut-ink outline-none placeholder:text-ut-mut',
            className,
          )}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          aria-label={show ? 'Hide password' : 'Show password'}
          className="px-3.5 py-2 text-xs font-bold tracking-[0.06em] text-ut-mut hover:text-ut-ink"
        >
          {show ? 'HIDE' : 'SHOW'}
        </button>
      </span>
    </label>
  );
};

/** Plain text/email input in the account-screen style. */
export const AuthInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { label: React.ReactNode; side?: React.ReactNode; invalid?: boolean }
>(({ label, side, invalid, className, ...rest }, ref) => (
  <label className={labelCls}>
    <FieldLabel label={label} side={side} />
    <input
      ref={ref}
      {...rest}
      aria-invalid={invalid || undefined}
      className={clsx(
        'rounded-[10px] border bg-ut-field px-3.5 py-[13px] font-ut-body text-[15px] font-medium normal-case tracking-normal text-ut-ink outline-none transition placeholder:text-ut-mut focus:border-ut-acc',
        invalid ? 'border-ut-bad' : 'border-ut-line',
        className,
      )}
    />
  </label>
));
AuthInput.displayName = 'AuthInput';

const SEGMENT_TONE = ['bg-ut-line', 'bg-ut-bad', 'bg-ut-acc', 'bg-ut-ok', 'bg-ut-ok'];

/** Four bars that fill red → gold → green, with the label underneath. */
export const StrengthMeter: React.FC<{ password: string; min?: number }> = ({ password, min = 6 }) => {
  const score = passwordScore(password, min);
  return (
    <div className="flex flex-col gap-[5px]" aria-live="polite">
      <div className="grid grid-cols-4 gap-1">
        {[1, 2, 3, 4].map((i) => (
          <span key={i} className={clsx('h-1 rounded-sm', i <= score ? SEGMENT_TONE[score] : 'bg-ut-line')} />
        ))}
      </div>
      <span className="text-xs text-ut-mut">{score === 1 ? `Too short (at least ${min})` : STRENGTH_LABELS[score]}</span>
    </div>
  );
};

/** "Passwords match" / "Passwords don't match" under the confirm field. */
export const MatchLine: React.FC<{ password: string; confirm: string }> = ({ password, confirm }) => {
  const state = matchState(password, confirm);
  return (
    <span className={clsx('min-h-[16px] text-xs', state === 'match' ? 'text-ut-ok' : 'text-ut-bad')} aria-live="polite">
      {state === 'match' ? 'Passwords match' : state === 'mismatch' ? 'Passwords don’t match' : ''}
    </span>
  );
};

/** Square tile with a single glyph: "@" for email, ✓ / ! for verification. */
export const AuthGlyph: React.FC<{ glyph: string; tone: 'acc' | 'ok' | 'bad' | 'mut'; size?: 'md' | 'lg' }> = ({ glyph, tone, size = 'md' }) => (
  <span
    aria-hidden
    className={clsx(
      'flex items-center justify-center border font-ut-display font-extrabold',
      size === 'lg' ? 'h-16 w-16 rounded-2xl text-[30px]' : 'h-14 w-14 rounded-[14px] text-[26px]',
      tone === 'acc' && 'border-ut-acc bg-ut-acc-soft text-ut-acc',
      tone === 'ok' && 'border-ut-ok bg-ut-ok/10 text-ut-ok',
      tone === 'bad' && 'border-ut-bad bg-ut-bad/10 text-ut-bad',
      tone === 'mut' && 'border-ut-mut bg-ut-card text-ut-mut',
    )}
  >
    {glyph}
  </span>
);

/** Gold "← Back to sign in" text link. */
export const BackToSignIn: React.FC<{ onClick?: () => void }> = ({ onClick }) =>
  onClick ? (
    <button type="button" onClick={onClick} className="text-center text-sm font-semibold text-ut-acc hover:text-ut-ink">
      ← Back to sign in
    </button>
  ) : (
    <Link to="/login" className="text-center text-sm font-semibold text-ut-acc hover:text-ut-ink">
      ← Back to sign in
    </Link>
  );

/** "or" with a rule either side. */
export const OrRule: React.FC = () => (
  <div className="flex items-center gap-2.5 text-xs text-ut-mut">
    <span className="h-px flex-1 bg-ut-line" />
    or
    <span className="h-px flex-1 bg-ut-line" />
  </div>
);

/**
 * "Check your email" after a sign-in link or a reset link has gone out.
 * `onResend` sends the same email again and returns an error message, or null.
 */
export const UtCheckEmail: React.FC<{
  email: string;
  kind: 'magic' | 'reset';
  onResend: () => Promise<string | null>;
  onBack?: () => void;
}> = ({ email, kind, onResend, onBack }) => {
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const resend = async () => {
    setState('sending');
    setError(null);
    const err = await onResend();
    if (err) {
      setError(err);
      setState('idle');
    } else {
      setState('sent');
    }
  };
  return (
    <UtAuthCard>
      <AuthGlyph glyph="@" tone="acc" />
      <AuthHeading
        title="Check your email"
        sub={
          <>
            {kind === 'reset' ? 'We’ve sent a password reset link to' : 'We’ve sent a sign-in link to'}{' '}
            <b className="text-ut-ink">{email}</b>. It works once and expires in an hour.
          </>
        }
      />
      <div className="flex flex-col gap-1.5 rounded-xl bg-ut-card px-3.5 py-3 text-[13px] leading-normal text-ut-mut">
        <b className="text-ut-ink">Can’t see it?</b>
        <span>Check spam, or make sure this is the email you registered with.</span>
      </div>
      {error && <AuthNotice tone="bad">{error}</AuthNotice>}
      <button
        type="button"
        onClick={resend}
        disabled={state !== 'idle'}
        className={clsx(
          'rounded-[11px] border border-ut-line p-[13px] text-center text-[15px] font-semibold transition hover:bg-ut-card2 disabled:cursor-default',
          state === 'sent' ? 'text-ut-ok' : 'text-ut-ink',
        )}
      >
        {state === 'sent' ? 'Sent again' : state === 'sending' ? 'Sending…' : 'Resend email'}
      </button>
      <BackToSignIn onClick={onBack} />
    </UtAuthCard>
  );
};
